import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Pencil, Trash2, ShieldAlert } from "lucide-react";
import { Button, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useDialogs } from "@/hooks/useDialogs";
import { ApiError } from "@/api/client";
import { relativeTime } from "@/components/ux/relativeTime";
import {
  useDeleteNote,
  useEditNote,
  useModerateDeleteNote,
  useNotesByOwner,
  usePostNote,
} from "../hooks/useNotes";
import type { NoteDto } from "../api/notes";

export interface NotesTabPanelProps {
  ownerType: string;
  ownerId: string;
  /** Page size for the feed. Defaults to 50 (spec §10). */
  pageSize?: number;
}

const COMPLIANCE_REASON_MIN = 5;

/**
 * Notes Module v1.0 §8 — the shared panel every entity detail page
 * renders inside its Notes tab (Layout Standard §4.7). Composer at top,
 * reverse-chronological feed below; per-row edit / delete affordances
 * are server-driven via `canEdit` / `canDelete` / `canModerate`.
 */
export function NotesTabPanel({ ownerType, ownerId, pageSize = 50 }: NotesTabPanelProps) {
  const [skip, setSkip] = useState(0);
  const [draft, setDraft] = useState("");
  const { toast } = useToast();

  const list = useNotesByOwner(ownerType, ownerId, { skip: 0, take: skip + pageSize });
  const post = usePostNote(ownerType, ownerId);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      await post.mutateAsync({ ownerType, ownerId, body });
      setDraft("");
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not post note.";
      toast({ message: msg, tone: "error" });
    }
  };

  const notes = list.data ?? [];
  const isEmpty = !list.isLoading && notes.length === 0;
  const hasMore = notes.length === skip + pageSize;

  return (
    <section
      aria-label="Notes"
      className="flex flex-col gap-space-4"
      data-testid="notes-tab-panel"
    >
      <div className="rounded-md border border-border-light bg-white-pure p-space-3 sticky top-0 z-10">
        <label
          htmlFor="notes-composer"
          className="block font-display text-label font-bold uppercase tracking-label text-onyx mb-space-1"
        >
          Post an internal note
        </label>
        <p className="text-small text-gray-mid m-0 mb-space-2">
          Share context, link to a document, or flag a question. Visible to everyone who
          can view this record.
        </p>
        <Textarea
          id="notes-composer"
          rows={3}
          value={draft}
          placeholder="Write a note…"
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex items-center justify-between mt-space-2">
          <span className="text-label text-gray-mid">
            Editable for 24 hours · never shared outside your team
          </span>
          <Button
            variant="primary"
            size="sm"
            disabled={post.isPending || draft.trim().length === 0}
            onClick={submit}
          >
            {post.isPending ? "Posting…" : "Post note"}
          </Button>
        </div>
      </div>

      {list.isLoading && (
        <div className="text-small text-gray-mid">Loading notes…</div>
      )}

      {isEmpty && (
        <div className="rounded-md border border-dashed border-border-light p-space-4 text-small text-gray-mid">
          No notes yet. Post the first one to leave context for the team.
        </div>
      )}

      {notes.length > 0 && (
        <ol className="flex flex-col gap-space-3 m-0 p-0 list-none">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              ownerType={ownerType}
              ownerId={ownerId}
            />
          ))}
        </ol>
      )}

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="text"
            size="sm"
            onClick={() => setSkip((s) => s + pageSize)}
            disabled={list.isFetching}
          >
            {list.isFetching ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </section>
  );
}

interface RowProps {
  note: NoteDto;
  ownerType: string;
  ownerId: string;
}

function NoteRow({ note, ownerType, ownerId }: RowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const { toast } = useToast();
  const dialogs = useDialogs();
  const edit = useEditNote(ownerType, ownerId);
  const remove = useDeleteNote(ownerType, ownerId);
  const moderate = useModerateDeleteNote(ownerType, ownerId);

  const initials = (note.authorDisplayName ?? "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onSave = async () => {
    const body = draft.trim();
    if (!body || body === note.body) {
      setEditing(false);
      return;
    }
    try {
      await edit.mutateAsync({ id: note.id, body });
      setEditing(false);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not update note.";
      toast({ message: msg, tone: "error" });
    }
  };

  const onAuthorDelete = async () => {
    const ok = await dialogs.confirm({
      title: "Delete note?",
      message: "Delete this note? It will be soft-deleted for audit.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(note.id);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not delete note.";
      toast({ message: msg, tone: "error" });
    }
  };

  const onComplianceDelete = async () => {
    const reason = await dialogs.prompt({
      title: "Delete (Compliance)",
      label: "Reason",
      placeholder: "Why is this note being removed?",
      confirmLabel: "Delete",
      validate: (v) =>
        v.trim().length < COMPLIANCE_REASON_MIN
          ? `Provide at least ${COMPLIANCE_REASON_MIN} characters.`
          : null,
    });
    if (!reason) return;
    try {
      await moderate.mutateAsync({ id: note.id, reason });
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not moderate note.";
      toast({ message: msg, tone: "error" });
    }
  };

  const wasEdited =
    note.modifiedAt &&
    new Date(note.modifiedAt).getTime() - new Date(note.createdAt).getTime() > 1000;

  return (
    <li
      // 🔴 The anchor the activity feed's note links land on. Those links are
      // built server-side as `…?tab=notes#note-{id}` and the fragment had no
      // target here, so a corrected route would still have dropped the reader
      // at the top of an unfiltered list with no indication which note they
      // came for. Fixing the route alone would have looked done and been half
      // an item. `scroll-mt-*` keeps the row clear of the sticky page header
      // when the browser jumps to it.
      id={`note-${note.id}`}
      className="scroll-mt-24 rounded-md border border-border-light bg-white-pure p-space-3"
      data-testid="note-row"
    >
      <div className="flex items-start gap-space-2">
        <span
          aria-hidden
          className="h-8 w-8 rounded-pill bg-gold text-onyx flex items-center justify-center text-label font-display font-bold shrink-0"
        >
          {initials || "?"}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-space-2 flex-wrap">
            <span className="font-semibold text-small text-onyx truncate">
              {note.authorDisplayName ?? "Unknown"}
            </span>
            {note.authorRoleCode && (
              <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
                {note.authorRoleCode}
              </span>
            )}
            <span className="text-label text-gray-mid">
              {relativeTime(note.createdAt)}
              {wasEdited ? " · edited" : ""}
            </span>
          </div>

          {editing ? (
            <div className="mt-space-2">
              <Textarea
                rows={3}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-label="Edit note body"
              />
              <div className="flex items-center gap-space-2 mt-space-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={edit.isPending || draft.trim().length === 0}
                  onClick={onSave}
                >
                  {edit.isPending ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => {
                    setDraft(note.body);
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none text-onyx mt-space-2">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.body}</ReactMarkdown>
            </div>
          )}

          {!editing && (note.canEdit || note.canDelete || note.canModerate) && (
            <div className="flex items-center gap-space-2 mt-space-2">
              {note.canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft(note.body);
                    setEditing(true);
                  }}
                  className="inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow hover:text-gold"
                >
                  <Pencil size={12} strokeWidth={1.5} aria-hidden /> Edit
                </button>
              )}
              {note.canDelete && note.canEdit && (
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={onAuthorDelete}
                  className="inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow hover:text-status-error disabled:opacity-60"
                >
                  <Trash2 size={12} strokeWidth={1.5} aria-hidden /> Delete
                </button>
              )}
              {note.canModerate && (
                <button
                  type="button"
                  disabled={moderate.isPending}
                  onClick={onComplianceDelete}
                  data-testid="moderate-delete-btn"
                  className="inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-status-error hover:text-status-error disabled:opacity-60"
                >
                  <ShieldAlert size={12} strokeWidth={1.5} aria-hidden /> Delete (Compliance)
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
